import React from "react"
import { Action, ActionType } from "@tenforce/toolbox-button-push-react"

import { useAppSelector, useAppDispatch } from "../../../hooks"
import { getItemDetailUrl } from "../../../util/UrlGenerator"
import env from "../../../env"

import ContextMenu from "../../common/contextmenu/components/ContextMenu"
import ContextMenuAction from "../../common/contextmenu/components/ContextMenuAction"
import ShareButton from "../../common/resource/ShareButton"
import BookmarkButton from "../../bookmarks/components/BookmarkButton"

import { hide, executeItemAction, executeStateTransition } from "../slice"
import QuickEditButton from "./QuickEditButton"
import { Labels } from "../../../util/Translations"
import { IButton, ICallButton } from "../../common/contextmenu"
import { ISpinnerElement } from "../index"
import { FlowControlDTO } from "../../../apis/models/fields"

const applicationPath = env.applicationPath

interface Props {
    getSpinnerElement: () => ISpinnerElement | undefined
}

export default function ContextMenuModal({
    getSpinnerElement,
}: Props): JSX.Element {
    const {
        item,
        isShown,
        isStateTransitionLoading,
        actions: { customActions, standardActions, buttons },
        shouldSkipOldUIBehavior = false,
    } = useAppSelector((state) => state.itemBoxContextMenu)
    const {
        AddBookmark,
        RemoveBookmark,
        ItemDetailsCopiedToClipboard,
        ShareButtonTooltip,
    } = Labels
    const dispatch = useAppDispatch()

    const hideContextMenu = (): void => {
        dispatch(hide())
    }

    const executeCallAction = (
        event: React.MouseEvent,
        button: IButton,
    ): void => {
        event.preventDefault()
        if (!item) {
            return
        }
        dispatch(
            executeItemAction({
                itemId: item.id,
                button: button as ICallButton,
                shouldSkipOldUIBehavior: shouldSkipOldUIBehavior,
            }),
        )
    }

    const changeStatus = (button: FlowControlDTO): void => {
        if (!item) {
            return
        }
        dispatch(
            executeStateTransition({
                itemId: item.id,
                button: button,
                targetElement: spinnerElement,
            }),
        )
    }

    const itemUrl = getItemDetailUrl(
        applicationPath,
        item?.relativePath,
        item?.id,
    )
    const sharedUrl = `${window.location.origin}${itemUrl}`
    const spinnerElement = getSpinnerElement()

    return (
        <>
            {!!item && item.id && (
                <ContextMenu
                    isModalShown={isShown}
                    hideModal={hideContextMenu}
                    isActionHidden={true}
                    modalId="itemBoxContextMenuModal"
                    targetElement={spinnerElement}
                >
                    <div className="tw-py-2">
                        {buttons.length > 0 && (
                            <div className="tw-px-2" id="flowControls">
                                {buttons.map((button: FlowControlDTO) => (
                                    <Action
                                        key={button.Code}
                                        isLoading={isStateTransitionLoading}
                                        actionType={ActionType.button}
                                        isTransparent={
                                            button.Type === "Dismissive" ||
                                            button.Type === "Secondary"
                                        }
                                        isSecondary={
                                            button.Type === "Dismissive"
                                        }
                                        onClick={(): void =>
                                            changeStatus(button)
                                        }
                                    >
                                        {button.FlowLabel}
                                    </Action>
                                ))}
                            </div>
                        )}

                        {buttons.length > 0 && customActions.length > 0 && (
                            <div className="separator-line is-dotted is-dimmed tw-py-2"></div>
                        )}

                        {customActions.map((action: IButton) => {
                            return (
                                <ContextMenuAction
                                    button={action}
                                    extraClasses="tw-py-2 tw-px-4"
                                    executeCallAction={executeCallAction}
                                    key={action.uuid}
                                />
                            )
                        })}

                        {(buttons.length > 0 || customActions.length > 0) && (
                            <div className="separator-line is-dotted is-dimmed tw-py-2"></div>
                        )}

                        {item.isEditable && (
                            <QuickEditButton
                                executeCallAction={executeCallAction}
                                extraClasses="tw-py-2 tw-px-4"
                            />
                        )}

                        <BookmarkButton
                            actionType={ActionType.block}
                            addBookmarkLabel={AddBookmark}
                            removeBookmarkLabel={RemoveBookmark}
                            bookmarkButtonId="ITEMBOX_BOOKMARK_BUTTON"
                            showCaption={true}
                            extraClasses="tw-py-2 tw-px-4"
                        />

                        <ShareButton
                            buttonId={`shareButton${item.id}`}
                            numberText={item.number}
                            resourceTitle={item.title ?? ""}
                            showCaption={true}
                            successMessage={ItemDetailsCopiedToClipboard}
                            tooltip={ShareButtonTooltip}
                            url={sharedUrl}
                            extraClasses="tw-px-4"
                        />

                        {standardActions.length > 0 && (
                            <>
                                <div className="separator-line is-dotted is-dimmed tw-py-2"></div>
                                {standardActions.flatMap((action: IButton) => {
                                    return [
                                        action.id === "DeleteItemAction" &&
                                            standardActions.length > 1 && (
                                                <div
                                                    key={`${action.uuid}separator`}
                                                    className="separator-line is-dotted is-dimmed tw-py-2"
                                                />
                                            ),
                                        <ContextMenuAction
                                            button={action}
                                            extraClasses="tw-px-4 tw-py-2"
                                            executeCallAction={
                                                executeCallAction
                                            }
                                            key={action.uuid}
                                        />,
                                    ]
                                })}
                            </>
                        )}
                    </div>
                </ContextMenu>
            )}
        </>
    )
}
